import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Paper, Typography, Grid, Chip, IconButton, Tooltip, TextField,
    InputAdornment, Button, CircularProgress, Stack, Menu, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, ThemeProvider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import TuneIcon from '@mui/icons-material/Tune';
import api, { create as apiCreate } from '../../../services/api';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import Swal from 'sweetalert2';
import CargoModal from './CargoModal';
import lightTheme from '../../../themes/lightTheme';

/**
 * Página /group — lista de cargos Radius (groupname distinct) em cards.
 * Click no card abre o CargoModal com os atributos do cargo.
 */
export default function CargosPage() {
    const notify = useNotify();
    const auth = useAuth();
    const [cargos, setCargos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [openCargo, setOpenCargo] = useState(null);  // groupname
    const [createOpen, setCreateOpen] = useState(false);
    const [renameTarget, setRenameTarget] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuTarget, setMenuTarget] = useState(null);

    const reload = async () => {
        setLoading(true);
        try {
            const res = await api.post('/groupreply/cargos/', {
                limit: 200, offset: 0, query: query || null,
            });
            setCargos(res.data?.data || []);
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao carregar cargos', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'IPV7 Tools - Cargos Radius';
        reload();
        // eslint-disable-next-line
    }, []);

    // Reload ao trocar query (debounce simples)
    useEffect(() => {
        const t = setTimeout(reload, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line
    }, [query]);

    const handleDelete = async (cargo) => {
        const r = await Swal.fire({
            title: `Remover cargo "${cargo.groupname}"?`,
            text: `Vai apagar ${cargo.attr_count} atributo(s).` +
                  (cargo.user_count > 0 ? ` Cargo tem ${cargo.user_count} usuário(s) — bloqueado.` : ''),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remover',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#aa2525',
            didOpen: () => {
                const c = document.querySelector('.swal2-container');
                if (c) c.style.zIndex = '99999';
            },
        });
        if (!r.isConfirmed) return;
        try {
            await api.delete(`/groupreply/cargo/${encodeURIComponent(cargo.groupname)}`);
            notify.setNotify({ open: true, message: 'Cargo removido', severity: 'success' });
            await reload();
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao remover', severity: 'error' });
        }
    };

    const handleRenameSubmit = async (newName) => {
        try {
            await api.post('/groupreply/cargo/rename', {
                old_name: renameTarget.groupname,
                new_name: newName.trim(),
            });
            notify.setNotify({ open: true, message: 'Cargo renomeado', severity: 'success' });
            setRenameTarget(null);
            await reload();
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao renomear', severity: 'error' });
        }
    };

    const openMenu = (e, cargo) => {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuTarget(cargo);
    };
    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuTarget(null);
    };

    const canCreate = auth.hasPerm('group.create');
    const canUpdate = auth.hasPerm('group.update');
    const canDelete = auth.hasPerm('group.delete');

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 100px)', p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                    Cargos Radius
                </Typography>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <TextField
                        size="small"
                        placeholder="Buscar cargo…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ width: 240 }}
                    />
                    <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setCreateOpen(true)}
                        disabled={!canCreate}
                    >
                        Novo cargo
                    </Button>
                </Stack>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Cada cargo agrupa atributos Radius (radgroupreply) aplicados aos usuários vinculados.
                Click num card pra ver/editar atributos.
            </Typography>

            {loading ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : cargos.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        {query ? 'Nenhum cargo encontrado.' : 'Nenhum cargo cadastrado.'}
                    </Typography>
                </Paper>
            ) : (
                <Grid container spacing={2}>
                    {cargos.map((c) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={c.groupname}>
                            <Paper
                                elevation={1}
                                onClick={() => setOpenCargo(c.groupname)}
                                sx={{
                                    p: 2,
                                    cursor: 'pointer',
                                    minHeight: 130,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1,
                                    borderLeft: '4px solid',
                                    borderColor: c.user_count > 0 ? 'success.main' : 'grey.400',
                                    transition: 'transform 0.1s, box-shadow 0.1s',
                                    '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Typography variant="h6" sx={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: 16, wordBreak: 'break-all' }}>
                                        {c.groupname}
                                    </Typography>
                                    <IconButton size="small" onClick={(e) => openMenu(e, c)} disabled={!canUpdate && !canDelete}>
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                                    <Chip
                                        size="small"
                                        icon={<TuneIcon />}
                                        label={`${c.attr_count} atributo(s)`}
                                        variant="outlined"
                                        color="primary"
                                    />
                                    <Chip
                                        size="small"
                                        icon={<GroupIcon />}
                                        label={`${c.user_count} usuário(s)`}
                                        variant="outlined"
                                        color={c.user_count > 0 ? 'success' : 'default'}
                                    />
                                </Stack>
                            </Paper>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Menu de ações do card */}
            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                <MenuItem
                    onClick={() => { setRenameTarget(menuTarget); closeMenu(); }}
                    disabled={!canUpdate}
                >
                    <EditIcon fontSize="small" sx={{ mr: 1 }} /> Renomear
                </MenuItem>
                <MenuItem
                    onClick={() => { handleDelete(menuTarget); closeMenu(); }}
                    disabled={!canDelete || (menuTarget?.user_count > 0)}
                    sx={{ color: 'error.main' }}
                >
                    <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Apagar cargo
                </MenuItem>
            </Menu>

            {/* Modal de detalhes do cargo */}
            <CargoModal
                open={!!openCargo}
                groupname={openCargo}
                onClose={() => { setOpenCargo(null); reload(); }}
                onChanged={reload}
            />

            {/* Dialog de criação de cargo */}
            <CreateCargoDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={(name) => {
                    setCreateOpen(false);
                    reload();
                    setOpenCargo(name);  // já abre pra adicionar attrs
                }}
            />

            {/* Dialog de renomear */}
            <RenameDialog
                target={renameTarget}
                onClose={() => setRenameTarget(null)}
                onSubmit={handleRenameSubmit}
            />
        </Box>
        </ThemeProvider>
    );
}

function CreateCargoDialog({ open, onClose, onCreated }) {
    const notify = useNotify();
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (open) setName(''); }, [open]);

    const submit = async () => {
        const n = name.trim();
        if (!n) return;
        setSaving(true);
        try {
            // Cria com 1 attr placeholder (Reply-Message). UI já abre o modal
            // pra operador continuar adicionando.
            await apiCreate('/groupreply/create', {
                groupname: n,
                attribute: 'Reply-Message',
                op: '+=',
                value: 'Bem-vindo',
            });
            notify.setNotify({ open: true, message: 'Cargo criado', severity: 'success' });
            onCreated(n);
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao criar cargo', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Novo cargo</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Cargos são criados com um atributo placeholder (Reply-Message).
                    Você poderá editar e adicionar outros atributos em seguida.
                </Typography>
                <TextField
                    autoFocus fullWidth size="small"
                    label="Nome do cargo"
                    placeholder="ex.: cliente-100M"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={saving || !name.trim()}>
                    {saving ? <CircularProgress size={18} /> : 'Criar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RenameDialog({ target, onClose, onSubmit }) {
    const [name, setName] = useState('');
    useEffect(() => { setName(target?.groupname || ''); }, [target]);
    const submit = () => {
        if (!name.trim() || name.trim() === target?.groupname) return;
        onSubmit(name);
    };
    return (
        <Dialog open={!!target} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>Renomear cargo</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    De <strong>{target?.groupname}</strong> para:
                </Typography>
                <TextField
                    autoFocus fullWidth size="small"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button variant="contained" onClick={submit} disabled={!name.trim() || name.trim() === target?.groupname}>
                    Renomear
                </Button>
            </DialogActions>
        </Dialog>
    );
}
